import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AcceptancesByParentRiskIdResponse } from '@risksmart-app/trpc/src/types';
import type { GetAcceptancesByParentRiskIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC acceptance data to match the GraphQL query structure
 */
function mapTrpcAcceptancesToGraphQL(
  trpcData: AcceptancesByParentRiskIdResponse
): GetAcceptancesByParentRiskIdQuery {
  return {
    acceptance: trpcData.acceptance,
  };
}

export const useGetAcceptancesByParentRiskId = (
  riskId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.acceptance.getByParentRiskId.queryOptions({ riskId }),
    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcAcceptancesToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
